import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { NOTIFICATION_TYPES, setMessage } from '../../../toasts';
import { handleAxiosErrorMessage } from '../../../../utils';

import {
  isCreateLeadInternalServerError,
  isLeadEmailExistsError,
  isLeadAlreadyCompletedError,
} from '@/libs/axios/leadAxiosInstance';

import { LEAD_MUTATION_KEYS } from '../utils/leadApi.constants';
import { createLead } from '../services';
import { mapCreateLeadResponse } from '../mappers';

export function useCreateLead() {
  const dispatch = useDispatch();
  const { t } = useTranslation(['onboarding', 'system']);
  const [isLeadAlreadyCompleted, setIsLeadAlreadyCompleted] = useState(false);
  const [isInternalServerError, setIsInternalServerError] = useState(false);
  const [serverErrorMessage, setServerErrorMessage] = useState(null);

  const mutation = useMutation({
    mutationKey: LEAD_MUTATION_KEYS.create,
    mutationFn: async ({ email }) => {
      const dto = await createLead({ email });
      return mapCreateLeadResponse(dto);
    },
    onSuccess: (result) => {
      if (!result.magicLinkSent) {
        const body =
          result.message && result.message !== ''
            ? result.message
            : t('create_account.errors.submit_failed', { ns: 'onboarding' });

        dispatch(
          setMessage({
            body,
            type: NOTIFICATION_TYPES.ERROR,
          })
        );
      }
    },
    onError: (error) => {
      if (isLeadAlreadyCompletedError(error)) return;
      if (isLeadEmailExistsError(error)) return;
      if (isCreateLeadInternalServerError(error)) return;
      const errorMessage = handleAxiosErrorMessage(error);
      dispatch(
        setMessage({
          body: t(errorMessage, { ns: 'system' }),
          type: NOTIFICATION_TYPES.ERROR,
        })
      );
    },
  });

  const resetSemanticErrorState = () => {
    setIsLeadAlreadyCompleted(false);
    setIsInternalServerError(false);
    setServerErrorMessage(null);
  };

  const submit = async ({ email }) => {
    resetSemanticErrorState();

    try {
      const result = await mutation.mutateAsync({ email });
      return result;
    } catch (error) {
      if (isLeadAlreadyCompletedError(error)) {
        setIsLeadAlreadyCompleted(true);
        return { leadAlreadyCompleted: true };
      }

      if (isLeadEmailExistsError(error)) {
        return {
          leadEmailExists: true,
          serverErrorMessage: 'create_account.errors.email_exists',
        };
      }

      if (isCreateLeadInternalServerError(error)) {
        setIsInternalServerError(true);
        setServerErrorMessage('create_account.errors.internal_server_error');
        return {
          internalServerError: true,
          serverErrorMessage: 'create_account.errors.internal_server_error',
        };
      }

      return { unknownError: true };
    }
  };

  return {
    submit,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isLeadAlreadyCompleted,
    isInternalServerError,
    serverErrorMessage,
  };
}
